import React, { useState, useEffect, Fragment } from 'react';
import { useT } from '../i18n.js';
import { downloadComplianceDocument, downloadInvoiceDocument, downloadInvoiceAttachment } from '../api.js';
import { BILLING_STATUS_META, CATEGORIES, COMPLIANCE_META, SUPPLIERS, catOf, fmtTHB, netPayableOf } from './data.jsx';
import { IconArrow, IconBank, IconBuilding, IconCal, IconChart, IconCheck, IconChevron, IconClock, IconDots, IconDownload, IconExt, IconEye, IconFile, IconFilter, IconLock, IconPlus, IconReceipt, IconSearch, IconShield, IconStar, IconTag, IconUpload, IconUsers, IconX } from './icons.jsx';
import { Avatar, CategoryTag, RatingBar, StatusPill } from './screens-internal.jsx';

import { useState as useStateF, useEffect as useEffectF } from 'react';


/* ============================================================
   SUPPLIER DETAIL — TAB CONTENT
   ============================================================ */
export function DetailTabContent({ tab, supplier, invoices = [], ratings = [], audits = [], users = [], compliance = [] }) {
  // ⚠ MODIFIED FROM PORT — was prototype mock data. Now renders live
  // per-vendor records (invoices, ratings, audits, KYC) passed in from ScreenDetail.
  const s = supplier || {};
  const vid = s.vendor_id || s.id;
  return <DetailTabContentLive tab={tab} supplier={s} vid={vid} invoices={invoices} ratings={ratings} audits={audits} users={users} compliance={compliance}/>;
}

function DetailTabContentLive({ tab, supplier, vid, invoices, ratings, audits, users, compliance = [] }) {
  const t = useT();
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
  };
  const [dlId, setDlId] = useState(null);
  const rowKey = (d) => d.kind === 'compliance' ? 'c:' + d.compliance_id
    : d.kind === 'invoice-pdf' ? 'i:' + d.invoice_id
    : 'p:' + d.invoice_id + ':' + d.attachment_id;
  const doDownloadDoc = async (d) => {
    const key = rowKey(d);
    setDlId(key);
    try {
      if (d.kind === 'compliance') await downloadComplianceDocument(d.compliance_id);
      else if (d.kind === 'invoice-pdf') await downloadInvoiceDocument(d.invoice_id);
      else await downloadInvoiceAttachment(d.invoice_id, d.attachment_id, d.name);
    } catch (e) { window.__toast?.(`Download failed: ${e.message}`, 'error'); }
    finally { setDlId(null); }
  };

  if (tab === "docs") {
    // Live: KYC/compliance docs (incl. vendor re-uploads) + invoice PDFs + POP attachments.
    const DOC_LABEL = {
      'business-registration': 'Business registration', 'tax_cert': 'VAT certificate',
      'national_id': 'National ID copy',
      'book_bank': 'Bank book', 'rate_card': 'Rate card', 'company_profile': 'Company profile',
      'insurance': 'Insurance', 'kyc': 'KYC document', 'contract': 'Contract', 'other': 'Other',
    };
    const rows = [];
    for (const c of (compliance || [])) {
      if (!c.drive_file_id) continue;
      rows.push({
        kind: 'compliance',
        compliance_id: c.compliance_id,
        name: DOC_LABEL[c.doc_type] || c.doc_type,
        type: 'KYC / compliance',
        size: 0,
        uploaded: c.issued_at,
        status: c.status,
      });
    }
    for (const i of invoices) {
      if (i.has_pdf || i.invoice_pdf_id) {
        rows.push({
          kind: 'invoice-pdf',
          invoice_id: i.invoice_id || i.id,
          name: i.pdf_filename || `${i.invoice_id || i.id}.pdf`,
          type: 'Invoice PDF',
          size: i.pdf_size || 0,
          uploaded: i.submitted_at || i.submitted,
        });
      }
      for (const a of (i.pop_attachments || [])) {
        rows.push({
          kind: 'pop',
          invoice_id: i.invoice_id || i.id,
          attachment_id: a.id,
          name: a.name,
          type: 'Proof of performance',
          size: a.size || 0,
          uploaded: i.submitted_at || i.submitted,
          mime: a.mime,
        });
      }
    }
    return (
      <div style={{padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 16}}>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <div className="t-eyebrow">{t('{n} documents', { n: rows.length })}</div>
        </div>

        {rows.length === 0 ? (
          <div style={{padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>
            {t('No documents on file yet.')}
          </div>
        ) : (
          <table className="tbl" style={{tableLayout: "fixed", width: "100%"}}>
            <colgroup>
              <col style={{width: 38}}/>
              <col/>
              <col style={{width: 150}}/>
              <col style={{width: 120}}/>
              <col style={{width: 110}}/>
              <col style={{width: 70}}/>
              <col style={{width: 48}}/>
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>{t('Document')}</th>
                <th>{t('Type')}</th>
                <th>{t('Invoice')}</th>
                <th>{t('Uploaded')}</th>
                <th className="right">{t('Size')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d, i) => (
                <tr key={i}>
                  <td>
                    <span style={{
                      width: 26, height: 26, borderRadius: "var(--radius-sm)",
                      background: "var(--interactive-secondary)",
                      fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-secondary)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}>{(String(d.name).split('.').pop() || (d.kind === 'compliance' ? 'KYC' : '')).toUpperCase().slice(0, 4)}</span>
                  </td>
                  <td>
                    <div style={{fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={d.name}>{d.name}</div>
                    {d.kind === 'compliance' && d.status && <div className="muted" style={{fontSize: 11, textTransform: "capitalize"}}>{String(d.status).replace('-30d', ' (≤30d)')}</div>}
                  </td>
                  <td className="muted" style={{fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{t(d.type)}</td>
                  <td className="mono muted" style={{fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{d.invoice_id || '—'}</td>
                  <td className="mono muted" style={{fontSize: 12, whiteSpace: "nowrap"}}>{fmtDate(d.uploaded)}</td>
                  <td className="right mono" style={{fontSize: 12, whiteSpace: "nowrap"}}>{d.size ? `${(d.size / 1024).toFixed(0)} KB` : '—'}</td>
                  <td>
                    <button className="icon-btn" title={t('Download')} disabled={dlId === rowKey(d)} onClick={() => doDownloadDoc(d)}>
                      {dlId === rowKey(d) ? '…' : <IconDownload size={14}/>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  if (tab === "projects") {
    // Live: collapse invoices to a per-PO row (treat one PO as one project).
    const byPo = new Map();
    for (const i of invoices) {
      const po = i.po_number || i.po || '(no PO)';
      if (!byPo.has(po)) {
        byPo.set(po, { po, project: i.project, lines: [], total: 0, active: false, firstAt: null, lastAt: null });
      }
      const row = byPo.get(po);
      row.lines.push(i);
      row.total += netPayableOf(i);
      if (['pm-review','finance-verify','approved-payment','scheduled'].includes(i.status)) row.active = true;
      const at = i.submitted_at ? new Date(i.submitted_at).getTime() : null;
      if (at) {
        if (!row.firstAt || at < row.firstAt) row.firstAt = at;
        if (!row.lastAt  || at > row.lastAt)  row.lastAt  = at;
      }
    }
    const projects = Array.from(byPo.values()).sort((a, b) => (b.lastAt || 0) - (a.lastAt || 0));
    // Map vendor's ratings keyed by engagement_ref (invoice id) → PO via lookup
    const ratingByInv = {};
    for (const r of ratings) {
      if (r.engagement_ref) ratingByInv[r.engagement_ref] = Number(r.overall_5) || null;
    }
    const totalSpend = projects.reduce((a, b) => a + b.total, 0);
    const activeCount = projects.filter(p => p.active).length;

    return (
      <div style={{padding: "16px 24px 24px"}}>
        <div className="stat-row" style={{padding: 0, marginBottom: 16, gridTemplateColumns: "repeat(3, 1fr)"}}>
          <div className="stat-tile">
            <div className="label">{t('Total projects')}</div>
            <div className="val">{projects.length}</div>
            <div className="delta muted">{t('{a} active · {b} closed', { a: activeCount, b: projects.length - activeCount })}</div>
          </div>
          <div className="stat-tile">
            <div className="label">{t('Lifetime value')}</div>
            <div className="val tnum">{fmtTHB(totalSpend)}</div>
            <div className="delta muted">{t('across {n} invoices', { n: invoices.length })}</div>
          </div>
          <div className="stat-tile">
            <div className="label">{t('Reviews submitted')}</div>
            <div className="val">{ratings.length}</div>
            <div className="delta muted">{ratings.length ? t('avg {v}/5', { v: (ratings.reduce((a,b)=>a+Number(b.overall_5||0),0)/ratings.length).toFixed(1) }) : t('no reviews yet')}</div>
          </div>
        </div>

        {projects.length === 0 ? (
          <div style={{padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>
            {t('No project activity yet. Projects appear here when the supplier submits invoices.')}
          </div>
        ) : (
          <table className="tbl" style={{tableLayout: "auto"}}>
            <thead>
              <tr>
                <th>{t('Project')}</th>
                <th>{t('PO')}</th>
                <th>{t('Period')}</th>
                <th className="right">{t('Lines')}</th>
                <th className="right">{t('Contract value')}</th>
                <th>{t('Status')}</th>
                <th>{t('Quality')}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p, i) => {
                const scoreVals = p.lines.map(l => ratingByInv[l.invoice_id || l.id]).filter(v => v);
                const score = scoreVals.length ? (scoreVals.reduce((a, b) => a + b, 0) / scoreVals.length) : null;
                return (
                  <tr key={i}>
                    <td style={{fontSize: 13}}>{p.project || '—'}</td>
                    <td className="mono" style={{fontSize: 12}}>{p.po}</td>
                    <td className="muted mono" style={{fontSize: 12}}>
                      {p.firstAt ? fmtDate(new Date(p.firstAt).toISOString()) : '—'}
                      {p.lastAt && p.lastAt !== p.firstAt ? ` – ${fmtDate(new Date(p.lastAt).toISOString())}` : ''}
                    </td>
                    <td className="right mono" style={{fontSize: 12}}>{p.lines.length}</td>
                    <td className="num right tnum">{fmtTHB(p.total)}</td>
                    <td>
                      <span className={"pill " + (p.active ? "pill-info" : "pill-neutral")} style={{fontSize: 10}}>
                        <span className="dot"></span>{p.active ? t('Active') : t('Closed')}
                      </span>
                    </td>
                    <td>
                      {score ? (
                        <span className="rating">
                          <span className="rating-bar">
                            {Array.from({length: 5}).map((_, j) => <i key={j} className={j < Math.round(score) ? "on" : ""}></i>)}
                          </span>
                          <span className="tnum mono" style={{fontSize: 11}}>{score.toFixed(1)}</span>
                        </span>
                      ) : (
                        <span className="muted" style={{fontSize: 11, fontStyle: "italic"}}>{t('not yet rated')}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  if (tab === "billing") {
    const bills = (invoices || []).slice().sort((a, b) => {
      const ta = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
      const tb = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
      return tb - ta;
    });
    const paidTotal = bills.filter(b => b.status === "paid").reduce((a, b) => a + netPayableOf(b), 0);
    const openTotal = bills.filter(b => b.status !== "paid" && b.status !== "rejected").reduce((a, b) => a + netPayableOf(b), 0);
    const cycles = bills
      .filter(b => b.status === 'paid' && b.submitted_at && b.paid_at)
      .map(b => (new Date(b.paid_at).getTime() - new Date(b.submitted_at).getTime()) / 86400000);
    const avgCycle = cycles.length ? (cycles.reduce((a, b) => a + b, 0) / cycles.length) : null;
    const openCount = bills.filter(b => ['pm-review','finance-verify'].includes(b.status)).length;
    const schedCount = bills.filter(b => ['approved-payment','scheduled'].includes(b.status)).length;
    return (
      <div style={{padding: "16px 24px 24px"}}>
        <div className="stat-row" style={{padding: 0, marginBottom: 16, gridTemplateColumns: "repeat(4, 1fr)"}}>
          <div className="stat-tile">
            <div className="label">{t('Invoices')}</div>
            <div className="val">{bills.length}</div>
            <div className="delta muted">{t('lifetime')}</div>
          </div>
          <div className="stat-tile">
            <div className="label">{t('Paid total')}</div>
            <div className="val tnum">{fmtTHB(paidTotal)}</div>
            <div className="delta muted">{t('{n} invoices', { n: bills.filter(b => b.status === "paid").length })}</div>
          </div>
          <div className="stat-tile">
            <div className="label">{t('Outstanding')}</div>
            <div className="val tnum" style={{color: "var(--info)"}}>{fmtTHB(openTotal)}</div>
            <div className="delta muted">{t('{a} in review · {b} scheduled', { a: openCount, b: schedCount })}</div>
          </div>
          <div className="stat-tile">
            <div className="label">{t('Avg cycle time')}</div>
            <div className="val">{avgCycle === null ? '—' : <>{avgCycle.toFixed(1)}<span style={{fontSize:18, color:"var(--text-tertiary)"}}>d</span></>}</div>
            <div className="delta muted">{t('{n} samples', { n: cycles.length })}</div>
          </div>
        </div>

        {bills.length === 0 ? (
          <div style={{padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>
            {t('No invoices on file yet.')}
          </div>
        ) : (
          <table className="tbl" style={{tableLayout: "auto"}}>
            <thead>
              <tr>
                <th>{t('Invoice')}</th>
                <th>{t('Project')}</th>
                <th>{t('Invoice no.')}</th>
                <th>{t('Submitted')}</th>
                <th className="right">{t('Amount')}</th>
                <th>{t('Status')}</th>
                <th className="right">{t('Cycle (d)')}</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b, i) => {
                const cycle = (b.status === 'paid' && b.submitted_at && b.paid_at)
                  ? (new Date(b.paid_at).getTime() - new Date(b.submitted_at).getTime()) / 86400000
                  : null;
                return (
                  <tr key={i}>
                    <td className="mono" style={{fontSize: 12, color: "var(--text-primary)"}}>{b.invoice_id || b.id}</td>
                    <td style={{fontSize: 13}}>{b.project || '—'}</td>
                    <td className="mono muted" style={{fontSize: 12}}>{b.po_number || b.po || '—'}</td>
                    <td className="muted mono" style={{fontSize: 12}}>{fmtDate(b.submitted_at || b.submitted)}</td>
                    <td className="num right tnum">{fmtTHB(netPayableOf(b))}</td>
                    <td><StatusPill meta={BILLING_STATUS_META[b.status]}/></td>
                    <td className="right mono" style={{fontSize: 12, color: cycle && cycle > 4 ? "var(--warning)" : "var(--text-secondary)"}}>{cycle === null ? '—' : cycle.toFixed(1)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  if (tab === "audit") {
    // Live: filter the global audit log to entries that reference this vendor.
    const events = (audits || []).filter(a => {
      if (!a) return false;
      if (a.entity_id === vid) return true;
      const blob = `${a.before_json || ''}${a.after_json || ''}${a.rawAction || ''}`;
      return blob.includes(vid);
    }).slice(0, 200);
    return (
      <div style={{padding: "16px 24px 24px"}}>
        {events.length === 0 ? (
          <div style={{padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>
            {t('No audit events recorded for this supplier yet.')}
          </div>
        ) : (
          <table className="tbl" style={{tableLayout: "auto"}}>
            <thead>
              <tr>
                <th style={{width: 130}}>{t('When')}</th>
                <th>{t('Actor')}</th>
                <th>{t('Action')}</th>
                <th>{t('Entity')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i}>
                  <td>
                    <div className="mono" style={{fontSize: 12}}>{e.date || fmtDate(e.at)}</div>
                    <div className="mono muted" style={{fontSize: 11, marginTop: 2}}>{e.t || ''}</div>
                  </td>
                  <td>
                    <div style={{display: "flex", alignItems: "center", gap: 8}}>
                      <Avatar name={e.actor || '?'} size="sm"/>
                      <div>
                        <div style={{fontSize: 13}}>{e.actor || '—'}</div>
                        <div className="muted" style={{fontSize: 11}}>{e.role || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{fontSize: 13}}>{e.rawAction || e.action || '—'}</td>
                  <td className="muted mono" style={{fontSize: 12}}>{e.entity_id || e.entity || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  return null;
}
